import React, { useState } from "react";
import { Col, Row } from "reactstrap";

import csvIcon from "@/assets/images/fileTypes/csv.webp";
import docIcon from "@/assets/images/fileTypes/doc.webp";
import pdfIcon from "@/assets/images/fileTypes/pdf.webp";
import pptIcon from "@/assets/images/fileTypes/ppt.webp";
import txtIcon from "@/assets/images/fileTypes/txt.webp";
import wordIcon from "@/assets/images/fileTypes/word.webp";
import xlsIcon from "@/assets/images/fileTypes/xls.webp";
import { formatDate } from "@/helpers/date_helper";

import { DateTimeLabel } from "./DateTimeLabel";
import DeleteModal from "./Modals/DeleteModal";
import FileViewer from "./Modals/FileViewer";
import { PhotoViewer } from "./PhotoViewer";

const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const isImage = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  return ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
};

const fileTypeIcons = {
  pdf: pdfIcon,
  csv: csvIcon,
  doc: docIcon,
  docx: wordIcon,
  ppt: pptIcon,
  pptx: pptIcon,
  txt: txtIcon,
  xls: xlsIcon,
  xlsx: xlsIcon,
};

const getFileTypeIcon = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  return fileTypeIcons[ext] || null;
};

const S3_BASE = process.env.REACT_APP_S3;

const getFullUrl = (path) => `${S3_BASE}${path}`;

const deleteBtnStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: "rgba(0,0,0,.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  border: "none",
  padding: 0,
};

const MultipleFileRendererV2 = ({ files = [], allowDelete = false, deleteService, deletePayload = {}, fileUniqueId = "fileId", onDeleteSuccess,xl=3, lg=4, md=6, sm=12 }) => {
  const [openPrint, setOpenPrint] = useState(false);
  const [printUrl, setPrintUrl] = useState("");
  const [printName, setPrintName] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const handlePrint = React.useCallback(() => setOpenPrint(false), []);

  const openFileViewer = (file) => {
    const fullRoute = getFullUrl(file.original);
    const ext = file.fileName.split(".").pop().toLowerCase();

    const viewerUrl = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)
      ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fullRoute)}`
      : fullRoute;

    setPrintUrl(viewerUrl);
    setOriginalUrl(getFullUrl(file.original));
    setPrintName(file.fileName);
    setOpenPrint(true);
  };

  const handleDeleteClick = (e, file) => {
    e.stopPropagation();
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteService || !fileToDelete) return;

    try {
      const fileId = fileToDelete[fileUniqueId];

      const res = await deleteService({
        [fileUniqueId]: fileId,
        ...deletePayload,
      }).unwrap();

      if (res?.success) {
        onDeleteSuccess?.(fileId, res);
        setShowDeleteModal(false);
        setFileToDelete(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  return (
    <>
      <FileViewer data={printUrl} originalUrl={originalUrl} onCloseClick={handlePrint} show={openPrint} moduleName={`View: ${printName}`} />

      <DeleteModal
        title={`Delete "${fileToDelete?.fileName || "File"}"`}
        show={showDeleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCancelDelete}
      />

      <Row className="g-3">
        {files.map((file) => {
          const isImg = isImage(file.fileName);
          const previewSrc = file.thumbnail || file.small || file.medium || file.original;
          const fullSrc = file.small || file.medium || file.large || file.original;

          return (
            <Col xl={xl} lg={lg} md={md} sm={sm} key={file[fileUniqueId]}>
              {isImg ? (
                <PhotoViewer src={getFullUrl(fullSrc)}>
                  <div
                    className="file-item d-flex border border-dashed p-2 rounded position-relative align-items-center cursor-pointer"
                    title={file.fileName}
                    style={{ minHeight: "70px" }}>
                    <div className="flex-shrink-0">
                      <div className="avatar-md">
                        <img
                          src={getFullUrl(previewSrc)}
                          alt={file.fileName}
                          className="rounded"
                          style={{ width: 40, height: 40, objectFit: "cover" }}
                        />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3 overflow-hidden">
                      <h6 className="mb-1 text-truncate fs-14 fw-medium">{file.fileName}</h6>
                      <p className="text-muted mb-0 fs-13">{formatSize(file.fileSize)}</p>
                      <DateTimeLabel value={file.dateUploaded} />
                    </div>
                    {allowDelete && (
                      <div style={deleteBtnStyle} onClick={(e) => handleDeleteClick(e, file)} title="Delete" className="bg-danger-subtle">
                        <i className="ri-delete-bin-2-line text-danger" style={{ fontSize: "14px" }}></i>
                      </div>
                    )}
                  </div>
                </PhotoViewer>
              ) : (
                <div
                  className="file-item d-flex border border-dashed p-2 rounded position-relative align-items-center cursor-pointer"
                  onClick={() => openFileViewer(file)}
                  title={file.fileName}
                  style={{ minHeight: "70px" }}>
                  <div className="flex-shrink-0">
                    <div className="avatar-md">
                      {getFileTypeIcon(file.fileName) ? (
                        <img
                          src={getFileTypeIcon(file.fileName)}
                          alt={file.fileName}
                          style={{ width: "40px", height: "40px", objectFit: "contain" }}
                        />
                      ) : (
                        <div className="avatar-title bg-light rounded">
                          <i className="ri-file-line fs-24 text-muted"></i>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3 overflow-hidden">
                    <h6 className="mb-1 text-truncate fs-14 fw-medium">{file.fileName}</h6>
                    <p className="text-muted mb-0 fs-13">{formatSize(file.fileSize)}</p>
                    <DateTimeLabel value={file.dateUploaded} />
                  </div>
                  {allowDelete && (
                    <div style={deleteBtnStyle} onClick={(e) => handleDeleteClick(e, file)} title="Delete" className="bg-danger-subtle">
                      <i className="ri-delete-bin-2-line text-danger" style={{ fontSize: "14px" }}></i>
                    </div>
                  )}
                </div>
              )}
            </Col>
          );
        })}
      </Row>

      <style>{`
        .file-item {
          transition: all 0.2s ease;
        }
        
        .file-item:hover {
          box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.075);
          transform: translateY(-2px);
        }

        .avatar-md {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .avatar-title {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fs-13 {
          font-size: 0.8125rem !important;
        }

        .fs-14 {
          font-size: 0.875rem !important;
        }

        .fs-24 {
          font-size: 1.5rem !important;
        }
      `}</style>
    </>
  );
};

export default React.memo(MultipleFileRendererV2);
